import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import {
  buildExportFilename, exportRowsToCSV, exportTableToFile,
} from '../../../shared/utils/tableExport';

import AddAthleteModal from './presentational/modals/AddAthleteModal';
import AddOptionsModal from './presentational/modals/AddOptionsModal';
import UploadUsersModal from './presentational/modals/UploadUsersModal';
import FilterAthletesModal from './presentational/modals/FilterAthletesModal';
import ExportDataModal from './presentational/modals/ExportDataModal';
import ManageAthletesTable from './presentational/tables/ManageAthletesTable';
import ManageAthletesBulkActions from './presentational/ManageAthletesBulkActions';
import ManageAthletesModals from './presentational/modals/ManageAthletesModals';
import ModalSupportNew from '../../../shared/components/ModalSupportNew';
import {
  fetchArchivedAthletes, fetchAthletes, fetchPrograms,
} from '../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HiddenTable = styled.table`
  display: none;
`;

const EMPTY_FILTERS = {
  filterMode: 'any',
  filterGroups: [],
  filterCalendars: [],
  filterGenders: [],
};

const DEFAULT_COLUMN_VISIBILITY = {
  userId: false,
  externalId: false,
  ssoId: false,
  smartabaseId: false,
  joinedDate: false,
};

const getBackLabel = (backPath, isMobile) => {
  if (isMobile && backPath.startsWith('/dashboard/athletes')) return 'Back to Athletes';
  if (backPath.startsWith('/dashboard/manage')) return 'Back to Manage';
  return 'Back to Home';
};

const ManageAthletes = ({ backPath, onEditAthlete, isMobile }) => {
  const history = useHistory();
  const location = useLocation();
  // A link from another manage page (e.g. a group's athlete count) can say where Back
  // should return to; otherwise fall back to the tab's last-visited path.
  const returnPath = location.state?.backPath || backPath;
  const returnLabel = location.state?.backLabel || getBackLabel(backPath, isMobile);
  const dispatch = useDispatch();

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const accountsList = useSelector((state) => state.auth.data.currentUser?.accountsList);
  const athletes = useSelector((state) => state.navigator.data.athletes);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const isLoadingCalendars = useSelector((state) => state.navigator.ui.isLoadingCalendars);
  const groups = useSelector((state) => state.navigator.data.groups);
  const isLoadingGroups = useSelector((state) => state.navigator.ui.isLoadingGroups);
  const programs = useSelector((state) => state.navigator.data.programs);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );
  const ssoEnabled = useSelector((state) => state.auth.data.organizationSettings?.sso?.enabled);
  const ssoLoginName = useSelector(
    (state) => state.auth.data.organizationSettings?.sso?.loginName,
  );
  const smartabaseEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.smartabaseEnabled,
  );
  const archivedUserSlots = useSelector(
    (state) => state.auth.data.organizationSettings?.organization?.archivedUserSlots ?? null,
  );
  const isLoadingAthletes = useSelector((state) => state.navigator.ui.isLoadingAthletes);

  const [athleteToDelete, setAthleteToDelete] = useState(null);
  const [athleteToResend, setAthleteToResend] = useState(null);
  const [athleteToArchive, setAthleteToArchive] = useState(null);
  const [athleteToRestore, setAthleteToRestore] = useState(null);
  const [isArchived, setIsArchived] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isAddAthleteModalOpen, setIsAddAthleteModalOpen] = useState(false);
  const [isAddOptionsModalOpen, setIsAddOptionsModalOpen] = useState(false);
  const [isUploadUsersModalOpen, setIsUploadUsersModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const [columnVisibility, setColumnVisibility] = useState(DEFAULT_COLUMN_VISIBILITY);
  const [visibleAthletes, setVisibleAthletes] = useState(athletes);

  // True when arriving via a calendar/group "Users" count deep link - keeps table loading
  const [isDeepLinkPending, setIsDeepLinkPending] = useState(
    () => Boolean(location.state?.filterCalendarId || location.state?.filterGroupId),
  );
  const [isDeepLinkFetching, setIsDeepLinkFetching] = useState(false);

  const hasActiveFilters = (
    activeFilters.filterGroups.length > 0
    || activeFilters.filterCalendars.length > 0
    || activeFilters.filterGenders.length > 0
  );

  useEffect(() => {
    if (accountCode) {
      batch(() => {
        if (!isDeepLinkPending) {
          dispatch(fetchAthletes(accountCode));
        }
        dispatch(fetchPrograms(accountCode));
      });
    }
  }, [accountCode, dispatch]);

  useEffect(() => {
    if (location.state?.openUploadModal) {
      setIsUploadUsersModalOpen(true);
      history.replace(location.pathname, { ...location.state, openUploadModal: false });
    }
  }, [location.state?.openUploadModal]);

  // Deep link from Manage Calendars' "Users" column - pre-filter to the clicked calendar.
  useEffect(() => {
    const filterCalendarId = location.state?.filterCalendarId;
    if (filterCalendarId == null || isLoadingCalendars) return;

    const matchedCalendar = calendars.find(
      (c) => String(c.id) === String(filterCalendarId),
    );
    if (matchedCalendar) {
      const newFilters = { ...EMPTY_FILTERS, filterCalendars: [matchedCalendar] };
      setActiveFilters(newFilters);
      dispatch(fetchAthletes(accountCode, newFilters));
      setIsDeepLinkFetching(true);
    } else {
      // Not found or the fetch failed — fall back to a plain fetch.
      setIsDeepLinkPending(false);
      dispatch(fetchAthletes(accountCode));
    }
    history.replace(location.pathname, { ...location.state, filterCalendarId: undefined });
  }, [location.state?.filterCalendarId, calendars, isLoadingCalendars, accountCode, dispatch]);

  // Same deep link from Manage Groups "Users" column - pre-filter to the selected group
  useEffect(() => {
    const filterGroupId = location.state?.filterGroupId;
    if (filterGroupId == null || isLoadingGroups) return;

    const matchedGroup = groups.find(
      (g) => String(g.id) === String(filterGroupId),
    );
    if (matchedGroup) {
      const newFilters = { ...EMPTY_FILTERS, filterGroups: [matchedGroup] };
      setActiveFilters(newFilters);
      dispatch(fetchAthletes(accountCode, newFilters));
      setIsDeepLinkFetching(true);
    } else {
      // Not found or the fetch failed — fall back to a plain fetch.
      setIsDeepLinkPending(false);
      dispatch(fetchAthletes(accountCode));
    }
    history.replace(location.pathname, { ...location.state, filterGroupId: undefined });
  }, [location.state?.filterGroupId, groups, isLoadingGroups, accountCode, dispatch]);

  // Clears the pending flag once the filtered fetch above actually resolves not when dispatched
  useEffect(() => {
    if (isDeepLinkFetching && !isLoadingAthletes) {
      setIsDeepLinkPending(false);
      setIsDeepLinkFetching(false);
    }
  }, [isDeepLinkFetching, isLoadingAthletes]);

  const refetchAthletes = () => {
    dispatch(fetchAthletes(accountCode, activeFilters));
    if (isArchived) {
      dispatch(fetchArchivedAthletes(accountCode));
    }
  };

  const refetchArchivedAthletes = () => {
    dispatch(fetchArchivedAthletes(accountCode));
  };

  const handleApplyFilters = ({ columnVisibility: newColVis, ...filters }) => {
    setActiveFilters(filters);
    setColumnVisibility(newColVis);
    setIsFilterOpen(false);
    dispatch(fetchAthletes(accountCode, filters));
  };

  const handleResetFilters = () => {
    setActiveFilters(EMPTY_FILTERS);
    setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
    setIsFilterOpen(false);
    dispatch(fetchAthletes(accountCode));
  };

  const calendarMap = useMemo(
    () => calendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {}),
    [calendars],
  );

  const exportColumns = useMemo(() => {
    if (isArchived) {
      return [
        { header: 'User ID', getValue: (a) => a.userId ?? '' },
        { header: 'First', getValue: (a) => a.first || '' },
        { header: 'Last', getValue: (a) => a.last || '' },
        {
          header: usernamesEnabled ? 'Username' : 'Email',
          getValue: (a) => (usernamesEnabled ? a.username : a.email) || '',
        },
      ];
    }

    const cols = [
      { header: 'First', getValue: (a) => a.first || '' },
      { header: 'Last', getValue: (a) => a.last || '' },
      {
        header: usernamesEnabled ? 'Username' : 'Email',
        getValue: (a) => (usernamesEnabled ? a.username : a.email) || '',
      },
    ];

    if (columnVisibility.userId) {
      cols.push({ header: 'User ID', getValue: (a) => a.id ?? '' });
    }
    if (columnVisibility.externalId) {
      cols.push({ header: 'External ID', getValue: (a) => a.externalId || '' });
    }
    if (columnVisibility.ssoId && ssoEnabled) {
      cols.push({ header: `${ssoLoginName || 'SSO'} ID`, getValue: (a) => a.ssoId || '' });
    }
    if (columnVisibility.smartabaseId && smartabaseEnabled) {
      cols.push({ header: 'Smartabase ID', getValue: (a) => a.smartabaseId || '' });
    }

    cols.push(
      { header: 'Groups', getValue: (a) => (a.groupAssignments || []).map((g) => g.name).join('; ') },
      { header: 'Calendar', getValue: (a) => calendarMap[a.calendarAssignment?.calendarId]?.name || '' },
    );

    if (columnVisibility.joinedDate) {
      cols.push({ header: 'Joined Date', getValue: (a) => a.joinedDate || '' });
    }

    cols.push({
      header: 'Last Activity',
      getValue: (a) => (a.activated === 1 ? (a.lastActivity || '') : 'Invited'),
    });

    return cols;
  }, [
    isArchived, columnVisibility, usernamesEnabled, ssoEnabled, ssoLoginName, smartabaseEnabled,
    calendarMap,
  ]);

  const exportToXLSX = () => {
    exportTableToFile('athletes-export-table', buildExportFilename('athletes', 'xlsx'), 'Athletes');
    setIsExportOpen(false);
  };

  const exportToXLS = () => {
    exportTableToFile('athletes-export-table', buildExportFilename('athletes', 'xls'), 'Athletes');
    setIsExportOpen(false);
  };

  const exportToCSV = () => {
    const headers = exportColumns.map((c) => c.header);
    const rows = visibleAthletes.map((a) => exportColumns.map((c) => c.getValue(a)));
    exportRowsToCSV(headers, rows, buildExportFilename('athletes', 'csv'));
    setIsExportOpen(false);
  };

  const addAthleteOptions = [
    {
      key: 'easy-join',
      title: 'Easy Join',
      description: 'Generate a shareable link and allow athletes to create their own profile.',
      icon: 'Link',
      onClick: () => history.push('/easy-join', { backPath: '/dashboard/manage-athletes', backLabel: 'Back to Manage Athletes' }),
    },
    {
      key: 'import-bulk',
      title: 'Import in Bulk',
      description: 'Create multiple athlete profiles using a spreadsheet.',
      icon: 'Users',
      onClick: () => {
        setIsAddOptionsModalOpen(false);
        setIsUploadUsersModalOpen(true);
      },
    },
    {
      key: 'one-at-a-time',
      title: 'Individual Athlete',
      description: 'Create a single athlete profile.',
      icon: 'add-user',
      onClick: () => {
        setIsAddOptionsModalOpen(false);
        setIsAddAthleteModalOpen(true);
      },
    },
  ];

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          <BackLink type='button' onClick={() => history.push(returnPath)}>
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            {returnLabel}
          </BackLink>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#1A1A1A' }}>
            Manage Athletes
          </Text>
        </TitleGroup>
        <ActionButtonGroup>
          <Button
            name='Export'
            icon='download'
            iconColor='#1A1A1A'
            customBorderRadius='4px'
            borderColor='#D6D6D6'
            txtColor='#1A1A1A'
            bgColor='var(--theme-bg-surface, #FFF)'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => setIsExportOpen(true)}
          />
          <Button
            name='Add Athletes'
            icon='add-user'
            iconColor='#FFF'
            txtColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            customBorderRadius='4px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => setIsAddOptionsModalOpen(true)}
          />
        </ActionButtonGroup>
      </TopSection>

      <BottomSection>
        <ManageAthletesTable
          onEditAthlete={onEditAthlete}
          onDeleteAthlete={setAthleteToDelete}
          onResendActivation={setAthleteToResend}
          onArchiveAthlete={setAthleteToArchive}
          onRestoreAthlete={setAthleteToRestore}
          isArchived={isArchived}
          onToggleArchived={setIsArchived}
          archivedUserSlots={archivedUserSlots}
          onOpenSupport={() => setIsSupportOpen(true)}
          onBulkAction={(action, ids) => {
            setBulkAction(action);
            setBulkSelectedIds(ids);
          }}
          columnVisibility={columnVisibility}
          onOpenFilter={() => setIsFilterOpen(true)}
          hasActiveFilters={hasActiveFilters}
          forceLoading={isDeepLinkPending}
          activeFiltersCount={
            [activeFilters.filterGroups, activeFilters.filterCalendars, activeFilters.filterGenders]
              .filter((a) => a.length > 0).length
          }
          onVisibleAthletesChange={setVisibleAthletes}
        />
      </BottomSection>

      <ExportDataModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        onExportXLSX={exportToXLSX}
        onExportXLS={exportToXLS}
        onExportCSV={exportToCSV}
      />

      <AddOptionsModal
        isOpen={isAddOptionsModalOpen}
        onClose={() => setIsAddOptionsModalOpen(false)}
        options={addAthleteOptions}
      />

      <ManageAthletesModals
        accountCode={accountCode}
        athleteToDelete={athleteToDelete}
        athleteToResend={athleteToResend}
        athleteToArchive={athleteToArchive}
        athleteToRestore={athleteToRestore}
        isArchived={isArchived}
        onClearDelete={() => setAthleteToDelete(null)}
        onClearResend={() => setAthleteToResend(null)}
        onClearArchive={() => setAthleteToArchive(null)}
        onClearRestore={() => setAthleteToRestore(null)}
        onRefetch={refetchAthletes}
        onRefetchArchived={refetchArchivedAthletes}
      />

      <ManageAthletesBulkActions
        bulkAction={bulkAction}
        bulkSelectedIds={bulkSelectedIds}
        onClose={() => { setBulkAction(null); setBulkSelectedIds([]); }}
        onSetBulkAction={setBulkAction}
        accountCode={accountCode}
        accountsList={accountsList}
        athletes={athletes}
        groups={groups}
        calendars={calendars}
        programs={programs}
        onRefetch={refetchAthletes}
      />

      <FilterAthletesModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        groups={groups}
        calendars={calendars}
        activeFilters={activeFilters}
        columnVisibility={columnVisibility}
      />

      <AddAthleteModal
        isOpen={isAddAthleteModalOpen}
        onClose={() => setIsAddAthleteModalOpen(false)}
        onCreated={() => refetchAthletes()}
      />

      <UploadUsersModal
        isOpen={isUploadUsersModalOpen}
        onClose={() => setIsUploadUsersModalOpen(false)}
        onDone={() => {
          setIsUploadUsersModalOpen(false);
          refetchAthletes();
        }}
      />

      <ModalSupportNew
        isOpen={isSupportOpen}
        onRequestClose={() => setIsSupportOpen(false)}
        path='/dashboard/manage-athletes'
      />

      <HiddenTable id='athletes-export-table'>
        <thead>
          <tr>
            {exportColumns.map((c) => <th key={c.header}>{c.header}</th>)}
          </tr>
        </thead>
        <tbody>
          {visibleAthletes.map((a) => (
            <tr key={a.id ?? a.userId}>
              {exportColumns.map((c) => <td key={c.header}>{c.getValue(a)}</td>)}
            </tr>
          ))}
        </tbody>
      </HiddenTable>
    </Container>
  );
};

ManageAthletes.propTypes = {
  backPath: PropTypes.string,
  onEditAthlete: PropTypes.func,
  isMobile: PropTypes.bool,
};

ManageAthletes.defaultProps = {
  backPath: '/dashboard/athletes',
  onEditAthlete: () => {},
  isMobile: false,
};

export default ManageAthletes;
